import React, { useState, useEffect, useRef } from "react";
import correctSound from "../../Assets/final new sound/correct-2-46134.mp3";
import incorrectSound from "../../Assets/final new sound/invalid-selection-39351.mp3";
import { useNavigate } from "react-router";
import { server_url } from "../../Config/API";
import AuthUser from "../../Hooks/authUser";
import moment from "moment";
import PostHooks from "../../Hooks/PostHooks";
import { useCallback } from "react";
import UpdateHooks from "../../Hooks/UpdateHooks";
import GameStatsIndicator from "./GameStatsIndicator";

function RapidMovement() {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const [currentLetter, setCurrentLetter] = useState("");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState(300);
  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const correctAudioRef = useRef(null);
  const incorrectAudioRef = useRef(null);
  if (typeof window !== "undefined") {
    if (!correctAudioRef.current) correctAudioRef.current = new Audio(correctSound);
    if (!incorrectAudioRef.current) incorrectAudioRef.current = new Audio(incorrectSound);
  }

  const [rapidData, setRapidData] = useState([]);
  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;

  const previousStateRef = useRef(null);
  const scoreRef = useRef(score);
  const timeRef = useRef(timeLeft);
  const currentLetterRef = useRef(currentLetter);
  const gameActiveRef = useRef(gameActive);

  scoreRef.current = score;
  timeRef.current = timeLeft;
  currentLetterRef.current = currentLetter;
  gameActiveRef.current = gameActive;

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/rapidMovement/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}&fieldName3=gameName&fieldValue3=Rapid+Movement+Monocular`
      );
      const data = await response.json();
      setRapidData(data?.data || []);
      if (data?.data && data?.data.length) {
        setScore(data?.data[0]?.score);
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching rapid movement data:", error);
    }
  };

  useEffect(() => {
    if (rapidData?.length) {
      setTimeLeft(rapidData[0]?.remainingTime);
      setScore(rapidData[0]?.score);
    }
  }, [rapidData]);

  useEffect(() => {
    fetchFunction();
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/rapidMovement/updateRapidMovement/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/rapidMovement/addRapidMovement`,
        {
          gameName: "Rapid Movement Monocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        },
        "Rapid movement score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const getRandomPosition = () => {
    return {
      x: Math.random() * (window.innerWidth - 100),
      y: Math.random() * (window.innerHeight - 200),
    };
  };

  const generateRandomLetter = () => {
    return letters[Math.floor(Math.random() * letters.length)];
  };

  const toggleFullScreenRef = useRef(null);
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.error("Error entering full screen:", error);
        });
      }
    }
    setIsFullScreen(!isFullScreen);
  };
  toggleFullScreenRef.current = toggleFullScreen;

  useEffect(() => {
    toggleFullScreen();
  }, []);

  const handleKeyPress = useCallback((event) => {
    if (gameActiveRef.current) {
      const pressedKey = event.key.toUpperCase();
      if (pressedKey === currentLetterRef.current) {
        if (correctAudioRef.current) {
          correctAudioRef.current.currentTime = 0;
          correctAudioRef.current.play().catch(e => console.log(e));
        }
        setScore((prevCount) => prevCount + 1);
        setCurrentLetter(generateRandomLetter());
        setPosition(getRandomPosition());
      } else {
        if (event.key !== "Escape") {
          if (incorrectAudioRef.current) {
            incorrectAudioRef.current.currentTime = 0;
            incorrectAudioRef.current.play().catch(e => console.log(e));
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          setGameActive(false);
          insertFunctionRef.current();
          navigate(-1);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [navigate]);

  useEffect(() => {
    if (gameActive) {
      setCurrentLetter(generateRandomLetter());
      setPosition(getRandomPosition());
    }
  }, [gameActive]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    const handleEscPress = async (event) => {
      if (event.keyCode === 27 && isFullScreen) {
        insertFunctionRef.current();
        await navigate(-1);
        if (toggleFullScreenRef.current) toggleFullScreenRef.current();
      }
    };

    document.addEventListener("keydown", handleEscPress);
    return () => {
      document.removeEventListener("keydown", handleEscPress);
    };
  }, [isFullScreen, navigate]);

  const min = Math.floor(timeLeft / 60);
  return (
    <div className="flex fixed inset-0 bg-white justify-center items-center h-screen">
      <GameStatsIndicator score={score} timeLeft={timeLeft} />
      {gameActive && (
        <div
          className="text-6xl font-bold"
          style={{
            position: "absolute",
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
        >
          {currentLetter}
        </div>
      )}
    </div>
  );
}

export default RapidMovement;
